import { Plus, Trash2 } from 'lucide-react';
import { useFormikContext } from 'formik';
import { cloneDeep } from 'lodash';
import { useState } from 'react';

import { Option } from '@@/form-components/PortainerSelect';
import { Button } from '@@/buttons';
import { createPersistedStore } from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import {
  EditableDatatable,
  NEW_ROW_ID,
} from '@@/datatables/editable/EditableDatatable';
import { isIdForNewRow } from '@@/datatables/editable/actionsColumn';

import type {
  KubernetesRbacFormValues,
  KubernetesUnifiedAccess,
} from './types';
import { useColumns } from './useColumns';

const accessStorageKey = 'k8s-rbac-access-list';
const accessSettingsStore = createPersistedStore(accessStorageKey);

interface AccessListDatatableProps {
  userOptions: Option<number>[];
  teamOptions: Option<number>[];
  roleOptions: Option<number>[];
  newIdCounter: number;
  setNewIdCounter: (newIdCounter: number) => void;
}

export function AccessListDatatable({
  userOptions,
  teamOptions,
  roleOptions,
  newIdCounter,
  setNewIdCounter,
}: AccessListDatatableProps) {
  const {
    values: { accessList = [] },
    setFieldValue,
  } = useFormikContext<KubernetesRbacFormValues>();
  const tableState = useTableState(accessSettingsStore, accessStorageKey);
  const [newEditableIdCounter, setNewEditableIdCounter] = useState(NEW_ROW_ID);

  return (
    <EditableDatatable
      title="Access list"
      dataset={accessList}
      columns={useColumns({
        accessList,
        onChange,
        onRemove: (item) => handleRemoveAccess([item]),
        userOptions,
        teamOptions,
        roleOptions,
      })}
      acceptRow={acceptRow}
      revertRow={revertRow}
      getRowId={(row) => `${row.Id}`}
      settingsManager={tableState}
      renderTableActions={(selectedRows) => (
        <>
          <Button
            icon={Plus}
            color="primary"
            size="small"
            onClick={addAccess}
            /* currently limit to 1 new row */
            disabled={accessList.some((row) => isIdForNewRow(row.Id))}
            data-cy="k8s-rbac-access-datatable-add-access-button"
            className="!ml-0"
          >
            Add Access
          </Button>
          <Button
            color="dangerlight"
            size="small"
            icon={Trash2}
            onClick={() => handleRemoveAccess(selectedRows)}
            data-cy="k8s-rbac-access-datatable-remove-access-button"
            disabled={selectedRows.length === 0}
          >
            Remove
          </Button>
        </>
      )}
      data-cy="k8s-rbac-access-datatable"
      emptyContentLabel="No access configured. Add access for users, teams and/or registries above."
      extendTableOptions={(opts) => ({ ...opts, autoResetPageIndex: false })}
    />
  );

  function onChange(val: KubernetesUnifiedAccess[]) {
    return setFieldValue('accessList', val);
  }

  function handleRemoveAccess(items: KubernetesUnifiedAccess[]) {
    const idsToRemove = new Set(
      items.map((item) => `${item.Type}-${item.PartyId}`)
    );
    const newAccessList = accessList.filter(
      (access) => !idsToRemove.has(`${access.Type}-${access.PartyId}`)
    );
    onChange(newAccessList);
  }

  function revertRow(rowIndex: number) {
    onChange([
      ...accessList.slice(0, rowIndex),
      ...accessList.slice(rowIndex + 1),
    ]);
  }

  function acceptRow(rowIndex: number) {
    const existingRowIndex = accessList.findIndex(
      (a, index) =>
        index !== rowIndex &&
        !isIdForNewRow(a.Id) &&
        a.Type === accessList[rowIndex].Type &&
        a.Name === accessList[rowIndex].Name
    );
    const newRow = cloneDeep(accessList[rowIndex]);

    if (existingRowIndex === -1) {
      newRow.Id = newIdCounter;
      setNewIdCounter(newIdCounter + 1);
      const newAccessList = [
        newRow,
        ...accessList.slice(0, rowIndex),
        ...accessList.slice(rowIndex + 1),
      ];
      onChange(newAccessList);
    } else {
      const newAccessList = [
        ...accessList.slice(0, rowIndex),
        ...accessList.slice(rowIndex + 1),
      ];
      newAccessList[existingRowIndex - 1].Role = newRow.Role;
      newAccessList[existingRowIndex - 1].namespaces = newRow.namespaces;
      onChange(newAccessList);
    }
  }

  function addAccess() {
    const newAccessRow: KubernetesUnifiedAccess = {
      Id: newEditableIdCounter,
      PartyId: null,
      Type: '',
      Name: '',
      Role: {
        Id: null,
        Name: '',
      },
    };

    const newAccessList = [cloneDeep(newAccessRow), ...accessList];
    onChange(newAccessList);
    setNewEditableIdCounter((count) => count - 1);
  }
}
